import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAuth0 } from '@auth0/auth0-react';
import AppLayout from '../components/layout/app-layout';
import PublicLayout from '../components/layout/public-layout';
import PMHomepage from './homepages/pm-homepage';
import TeamLeadHomepage from './homepages/team-lead-homepage';
import TeamMemberHomepagePage from './homepages/team-member-homepage';
import AdminHomepage from './homepages/admin-homepage';
import DepartmentLeadHomepage from './homepages/department-lead-homepage';
import PortfolioHealth from './reporting/portfolio-health';
import ProjectCapital from './projects/project-capital';
import AdminConfig from './admin/admin-config';
import Index from './index';
import IdeasPublic from './ideas-public';
import Governance from './governance';
import PortfolioDashboard from './portfolio-dashboard';
import HealthReports from './reporting/health-report';
import PendingTimesheets from './timesheets/pending-timesheets';
import TimesheetsPage from './timesheets/timesheets-page';
import TeamManagement from './admin/team-management';
import Roadmap from './roadmap';
import SubmitIdea from './public/submit-idea';
import DomainIdeas from './domain-owner/domain-ideas';
import BusinessCase from './projects/business-case';
import ForumAgenda from './governance/forum-agenda';
import DiscoveryKanban from './discovery/discovery-kanban';
import PipelinePage from './pipeline';

interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

// Routes that don't require authentication
const PUBLIC_PATHS = ['/ideas', '/roadmap', '/submit-idea'];

export default function Home() {
  const { user, loading: authLoading, login, logout } = useAuth();
  const { isLoading: auth0Loading, error: auth0Error } = useAuth0();
  const [loggingIn, setLoggingIn] = useState(false);
  const [currentPath, setCurrentPath] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/') ? hash.substring(1) : hash.substring(1) || '/';
  });

  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const path = hash.startsWith('#/') ? hash.substring(1) : hash.substring(1) || '/';
      setCurrentPath(path);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleLogin = () => {
    login(); // Auth0 will handle the redirect
  };

  const handleLogout = () => {
    logout();
  };

  // Handle Auth0 callback redirect
  if (window.location.pathname === '/auth0-callback') {
    if (auth0Loading) {
      return <div className="loading">Processing login...</div>;
    }
    if (auth0Error) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Login Error</h2>
          <p>{auth0Error.message}</p>
          <a href="/">Back to Home</a>
        </div>
      );
    }
    // Auth0 processing complete, redirect to home
    if (!auth0Loading) {
      window.location.href = '/#/';
      return <div className="loading">Redirecting...</div>;
    }
  }

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  // Handle public routes - accessible to both authenticated and unauthenticated users
  if (PUBLIC_PATHS.includes(currentPath)) {
    const publicRouteMap: { [key: string]: { component: React.ReactNode } } = {
      '/ideas': { component: <IdeasPublic /> },
      '/roadmap': { component: <Roadmap /> },
      '/submit-idea': { component: <SubmitIdea /> },
    };

    const publicRoute = publicRouteMap[currentPath];
    if (publicRoute) {
      // If authenticated, show in AppLayout; otherwise show in PublicLayout
      if (user) {
        return (
          <AppLayout user={user} onLogout={handleLogout}>
            {publicRoute.component}
          </AppLayout>
        );
      } else {
        return (
          <PublicLayout onLoginClick={() => window.location.hash = '#/'}>
            {publicRoute.component}
          </PublicLayout>
        );
      }
    }
  }

  if (!user) {
    return (
      <div style={{ maxWidth: '400px', margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
        <h1 style={{ marginBottom: '2rem' }}>Portfolio Management Tool</h1>
        <p style={{ marginBottom: '1.5rem', color: '#666' }}>Sign in with Auth0:</p>
        <button
          onClick={handleLogin}
          disabled={loggingIn}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: loggingIn ? 'not-allowed' : 'pointer',
            opacity: loggingIn ? 0.6 : 1,
          }}
        >
          {loggingIn ? 'Redirecting to Auth0...' : 'Sign In with Auth0'}
        </button>
      </div>
    );
  }

  // If a path is selected, show that page
  if (currentPath && currentPath !== '/') {
    const pathTitle = currentPath.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    // Route to appropriate page based on path
    const routeMap: { [key: string]: { title: string; subtitle: string; breadcrumbs: Breadcrumb[]; component: React.ReactNode } } = {
      '/ideas': {
        title: 'Ideas & Feedback',
        subtitle: 'Discover and discuss new ideas',
        breadcrumbs: [{ label: 'Discovery' }, { label: 'Ideas' }],
        component: <IdeasPublic />,
      },
      '/roadmap': {
        title: 'Roadmap',
        subtitle: 'View planned and in-progress initiatives',
        breadcrumbs: [{ label: 'Discovery' }, { label: 'Roadmap' }],
        component: <Roadmap />,
      },
      '/assessment': {
        title: 'Governance Assessment',
        subtitle: 'Review and assess ideas',
        breadcrumbs: [{ label: 'Governance' }, { label: 'Assessment' }],
        component: <Governance />,
      },
      '/gate-decisions': {
        title: 'Gate Decisions',
        subtitle: 'Make funding and approval decisions',
        breadcrumbs: [{ label: 'Governance' }, { label: 'Gate Decisions' }],
        component: <Governance />,
      },
      '/forum-agenda': {
        title: 'Governance Forum',
        subtitle: 'Review ideas ready for governance',
        breadcrumbs: [{ label: 'Governance' }, { label: 'Forum Agenda' }],
        component: <ForumAgenda />,
      },
      '/business-case': {
        title: 'Business Case',
        subtitle: 'Define project business case',
        breadcrumbs: [{ label: 'Governance' }, { label: 'Business Case' }],
        component: <BusinessCase />,
      },
      '/domain-ideas': {
        title: 'Domain Ideas',
        subtitle: 'Ideas for your domain',
        breadcrumbs: [{ label: 'Discovery' }, { label: 'Domain Ideas' }],
        component: <DomainIdeas />,
      },
      '/discovery-kanban': {
        title: 'Discovery Kanban',
        subtitle: 'Manage ideas through discovery phases',
        breadcrumbs: [{ label: 'Discovery' }, { label: 'Discovery Kanban' }],
        component: <DiscoveryKanban />,
      },
      '/pipeline': {
        title: 'Portfolio Pipeline',
        subtitle: 'View all ideas and projects across all phases',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Pipeline' }],
        component: <PipelinePage />,
      },
      '/active-projects': {
        title: 'Active Projects',
        subtitle: 'Projects in delivery',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Projects' }],
        component: <PortfolioDashboard />,
      },
      '/portfolio': {
        title: 'Portfolio View',
        subtitle: 'Complete project portfolio',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Overview' }],
        component: <PortfolioDashboard />,
      },
      '/my-projects': {
        title: 'My Projects',
        subtitle: 'Your assigned work',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'My Projects' }],
        component: <PortfolioDashboard />,
      },
      '/team-projects': {
        title: 'Team Projects',
        subtitle: 'Team deliverables and status',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Team Projects' }],
        component: <PortfolioDashboard />,
      },
      '/timesheets': {
        title: 'Submit Timesheet',
        subtitle: 'Log your hours for the week',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Timesheets' }],
        component: <TimesheetsPage />,
      },
      '/team-capacity': {
        title: 'Team Capacity',
        subtitle: 'Manage allocation and workload',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Team Capacity' }],
        component: <Index />,
      },
      '/pending-timesheets': {
        title: 'Pending Timesheet Approvals',
        subtitle: 'Review team submissions',
        breadcrumbs: [{ label: 'Portfolio' }, { label: 'Timesheets' }],
        component: <PendingTimesheets />,
      },
      '/my-hours': {
        title: 'My Hours',
        subtitle: 'Your timesheet summary',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'My Hours' }],
        component: <PortfolioHealth />,
      },
      '/team-health': {
        title: 'Team Health',
        subtitle: 'Team status and metrics',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'Team Health' }],
        component: <PortfolioHealth />,
      },
      '/analytics': {
        title: 'Analytics',
        subtitle: 'Project and resource analytics',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'Analytics' }],
        component: <PortfolioHealth />,
      },
      '/health-reports': {
        title: 'Health Reports',
        subtitle: 'Project health assessments',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'Health Reports' }],
        component: <HealthReports />,
      },
      '/portfolio-analytics': {
        title: 'Portfolio Analytics',
        subtitle: 'Portfolio-wide insights',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'Portfolio Analytics' }],
        component: <PortfolioHealth />,
      },
      '/health-dashboard': {
        title: 'Health Dashboard',
        subtitle: 'Overall portfolio health',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'Health Dashboard' }],
        component: <PortfolioHealth />,
      },
      '/dept-analytics': {
        title: 'Department Analytics',
        subtitle: 'Department insights and metrics',
        breadcrumbs: [{ label: 'Reporting' }, { label: 'Dept Analytics' }],
        component: <PortfolioHealth />,
      },
      '/admin': {
        title: 'Administration',
        subtitle: 'Manage system configuration',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Overview' }],
        component: <AdminHomepage />,
      },
      '/admin-config': {
        title: 'System Configuration',
        subtitle: 'Configure departments, GL codes, and project managers',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Configuration' }],
        component: <AdminConfig />,
      },
      '/departments': {
        title: 'Departments',
        subtitle: 'Manage organizational structure',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Departments' }],
        component: <AdminConfig />,
      },
      '/teams': {
        title: 'Teams',
        subtitle: 'Manage teams and assignments',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Teams' }],
        component: <TeamManagement />,
      },
      '/project-managers': {
        title: 'Project Managers',
        subtitle: 'Assign project ownership',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Project Managers' }],
        component: <AdminConfig />,
      },
      '/work-categories': {
        title: 'Work Categories',
        subtitle: 'Configure work types and GL codes',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Work Categories' }],
        component: <AdminConfig />,
      },
      '/strategic-goals': {
        title: 'Strategic Goals',
        subtitle: 'Define organizational strategy',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Strategic Goals' }],
        component: <AdminConfig />,
      },
      '/capital-projects': {
        title: 'Capital Projects',
        subtitle: 'Manage capital initiatives',
        breadcrumbs: [{ label: 'Admin' }, { label: 'Capital Projects' }],
        component: <ProjectCapital />,
      },
      '/submit-idea': {
        title: 'Submit an Idea',
        subtitle: 'Share your ideas for new features or improvements',
        breadcrumbs: [{ label: 'Discovery' }, { label: 'Submit Idea' }],
        component: <SubmitIdea />,
      },
    };

    const route = routeMap[currentPath];
    if (route) {
      return (
        <AppLayout
          user={user}
          title={route.title}
          subtitle={route.subtitle}
          breadcrumbs={route.breadcrumbs}
          onLogout={handleLogout}
        >
          {route.component}
        </AppLayout>
      );
    }

    // Fallback for unmapped routes
    return (
      <AppLayout
        user={user}
        title={pathTitle}
        subtitle="Navigate using the sidebar menu"
        breadcrumbs={[{ label: pathTitle }]}
        onLogout={handleLogout}
      >
        <div style={{ padding: '2rem', background: 'white', borderRadius: '8px', marginBottom: '2rem' }}>
          <p style={{ color: '#64748b' }}>Page not found. Use the sidebar to navigate.</p>
          <button
            onClick={() => window.location.hash = ''}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: '#06b6d4',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </AppLayout>
    );
  }

  // Route based on user role
  switch (user.Role) {
    case 'flow_ProjectManager':
      return (
        <AppLayout
          user={user}
          title="Portfolio Governance"
          subtitle="Manage ideas from discovery through execution"
          breadcrumbs={[
            { label: 'Portfolio Governance' },
            { label: 'PM Dashboard' },
          ]}
          onLogout={handleLogout}
        >
          <PMHomepage />
        </AppLayout>
      );

    case 'flow_TeamLead':
      return (
        <AppLayout
          user={user}
          title="Team Lead Dashboard"
          subtitle="Manage team capacity and pending timesheets"
          breadcrumbs={[
            { label: 'Portfolio' },
            { label: 'Team Dashboard' },
          ]}
          onLogout={handleLogout}
        >
          <TeamLeadHomepage />
        </AppLayout>
      );

    case 'flow_TeamMember':
      return (
        <AppLayout
          user={user}
          title="Team Member Dashboard"
          subtitle="Submit timesheets and track your workload"
          breadcrumbs={[
            { label: 'Portfolio' },
            { label: 'Dashboard' },
          ]}
          onLogout={handleLogout}
        >
          <TeamMemberHomepagePage />
        </AppLayout>
      );

    case 'flow_DepartmentLead':
      return (
        <AppLayout
          user={user}
          title="Department Dashboard"
          subtitle="Oversee department projects and resources"
          breadcrumbs={[
            { label: 'Admin' },
            { label: 'Department' },
          ]}
          onLogout={handleLogout}
        >
          <DepartmentLeadHomepage />
        </AppLayout>
      );

    case 'flow_Admin':
      return (
        <AppLayout
          user={user}
          title="Administration"
          subtitle="Manage users, teams, and system settings"
          breadcrumbs={[
            { label: 'Admin' },
            { label: 'Settings' },
          ]}
          onLogout={handleLogout}
        >
          <AdminHomepage />
        </AppLayout>
      );

    case 'Leadership':
    case 'flow_GovernanceCommittee':
      return (
        <AppLayout
          user={user}
          title="Portfolio Health"
          subtitle="Overview of all projects and status"
          breadcrumbs={[
            { label: 'Reporting' },
            { label: 'Portfolio Health' },
          ]}
          onLogout={handleLogout}
        >
          <PortfolioHealth />
        </AppLayout>
      );

    case 'flow_DomainOwner':
      return (
        <AppLayout
          user={user}
          title="Domain Ideas"
          subtitle="Manage ideas in your domain"
          breadcrumbs={[
            { label: 'Discovery' },
            { label: 'Domain Ideas' },
          ]}
          onLogout={handleLogout}
        >
          <DomainIdeas />
        </AppLayout>
      );

    case 'flow_FunctionLead':
      return (
        <AppLayout
          user={user}
          title="Function Lead Dashboard"
          subtitle="Oversee function governance and planning"
          breadcrumbs={[
            { label: 'Governance' },
            { label: 'Dashboard' },
          ]}
          onLogout={handleLogout}
        >
          <Index />
        </AppLayout>
      );

    case 'flow_PublicUser':
      return (
        <AppLayout
          user={user}
          title="My Ideas"
          subtitle="Ideas you've submitted"
          breadcrumbs={[
            { label: 'Discovery' },
            { label: 'My Ideas' },
          ]}
          onLogout={handleLogout}
        >
          <Index />
        </AppLayout>
      );

    case 'flow_SuperUser':
      return (
        <AppLayout
          user={user}
          title="SuperUser Dashboard"
          subtitle="Complete system access and control"
          breadcrumbs={[
            { label: 'Admin' },
            { label: 'SuperUser' },
          ]}
          onLogout={handleLogout}
        >
          <AdminHomepage />
        </AppLayout>
      );

    default:
      return <Index />;
  }
}

